/* One product's input card: find it by barcode (typed, scanned or from a
   photo), and fall back to a photo of the ingredients panel when Open Food
   Facts doesn't have its ingredient list.

   found         → ingredients and nutrition come from Open Food Facts, whose pack
                   photos are shown; no upload needed
   anything else → the card says why, and opens the label photo section: the
                   ingredients, nutrition and front-of-pack photos are all required */

import { describeFailure, lookupProduct } from "./api";
import { escapeHtml, html, qs } from "./dom";
import { NOVA_SHORT, novaColour } from "./grade";
import { cropPhoto } from "./crop";
import { icon } from "./icons";
import { shrinkPhoto } from "./photo";
import { scanBarcode } from "./scan-dialog";
import { cameraAvailable, scanFromPhoto, validBarcode } from "./scanner";
import type { NovaGroup, ProductLookup } from "./types";

/** What a finished input carries over to another screen ("Compare this"). */
export interface InputPreset {
  barcode?: string;
  ingredients?: File;
  nutrition?: File;
  front?: File;
  name?: string;
  brand?: string;
}

export interface InputOptions {
  /** Short tag in the corner, e.g. "A" or "Khetika". */
  tag: string;
  title: string;
  subtitle: string;
  tone?: "khetika" | "competitor" | "neutral";
  onChange: () => void;
}

let counter = 0;

type PhotoSlot = "ingredients" | "nutrition" | "front";

/** What the crop screen asks the user to frame, per photo. */
const CROP_TITLES: Record<PhotoSlot, string> = {
  ingredients: "Crop to the ingredient list",
  nutrition: "Crop to the nutrition table",
  front: "Crop to the front of the pack",
};

type StatusTone = "busy" | "ok" | "warn" | "err" | "";

/** The pack photos Open Food Facts (or our own database, for products learnt from photos) holds,
 *  so the user can check the data against them. */
function offImages(p: ProductLookup): string {
  const own = p.source === "CleanScore database";
  const shots: [string, string][] = [
    ["Front of pack", p.image_front_url],
    ["Ingredients", p.image_ingredients_url],
    ["Nutrition", p.image_nutrition_url],
  ];
  const figures = shots
    .map(([label, url]) =>
      url
        ? `<a class="off-shot" href="${escapeHtml(url)}" target="_blank" rel="noopener">` +
          `<img src="${escapeHtml(url)}" alt="${label} photo from ${own ? "CleanScore" : "Open Food Facts"}" loading="lazy"><span>${label}</span></a>`
        : `<div class="off-shot none"><span>${label}</span><small>${own ? "not saved" : "not on Open Food Facts"}</small></div>`,
    )
    .join("");
  return `<div class="found-images">${figures}</div>`;
}

export class ProductInput {
  readonly root: HTMLElement;
  lookup: ProductLookup | null = null;
  ingredients: File | null = null;
  nutrition: File | null = null;
  front: File | null = null;
  private readonly opts: InputOptions;
  private readonly id = "pin" + ++counter;

  constructor(opts: InputOptions) {
    this.opts = opts;
    this.root = this.build();
    this.wire();
  }

  /** Ready to analyse: OFF has the product, or its barcode plus the
   *  ingredients and front of pack photos (nutrition panel optional). The barcode is required so what is read from the photos is
   *  saved to the products table and found next time. */
  ready(): boolean {
    return !this.missing();
  }

  /** What still has to be added before analysing; "" when ready. */
  missing(): string {
    if (this.found()) return "";
    const photos = [this.ingredients, this.nutrition, this.front].filter(Boolean).length;
    if (!this.barcode()) {
      return photos ? "Add the product's barcode too, so it is saved for next time"
                    : "Scan or type the product's barcode";
    }
    if (!this.ingredients && !this.front) return "Add the ingredients and front of pack photos";
    if (!this.ingredients) return "Add the ingredients photo";
    if (!this.front) return "Add the front of pack photo";
    return "";
  }

  /** How many photos the server will have to read (drives the progress sheet). */
  photoCount(): number {
    if (this.found()) return 0;
    return [this.ingredients, this.nutrition, this.front].filter(Boolean).length;
  }

  found(): boolean {
    return this.lookup?.status === "found";
  }

  /** Name the user typed, else Open Food Facts' name. */
  name(): string {
    const typed = qs<HTMLInputElement>(this.root, ".pin-name").value.trim();
    return typed || this.lookup?.name || "";
  }

  /** Looked-up barcode, else a valid one typed in the box but never looked up. */
  barcode(): string {
    if (this.lookup?.barcode) return this.lookup.barcode;
    const typed = qs<HTMLInputElement>(this.root, ".pin-code").value.replace(/\D/g, "");
    return /^\d{8,14}$/.test(typed) && (!/^(\d{8}|\d{12}|\d{13})$/.test(typed) || validBarcode(typed))
      ? typed : "";
  }

  /** Append this product's fields to the request, under `prefix`. */
  appendTo(form: FormData, prefix: string): void {
    // The barcode goes up even when Open Food Facts lacks it, so the server can
    // keep the photo's ingredients against it and find them next time.
    const barcode = this.barcode();
    if (barcode) form.append(`${prefix}_barcode`, barcode);
    if (!this.found()) {
      if (this.ingredients) form.append(prefix, this.ingredients);
      if (this.nutrition) form.append(`${prefix}_nutrition`, this.nutrition);
      // A found product already has its data: no photos go up for it.
      if (this.front) form.append(`${prefix}_front`, this.front);
    }
    const name = this.name();
    if (name) form.append(`${prefix}_name`, name);
    // Left blank, the name and brand are read off the front of the pack.
    const brand = qs<HTMLInputElement>(this.root, ".pin-brand").value.trim();
    if (brand) form.append(`${prefix}_brand`, brand);
  }

  /** Data to rebuild this input on another screen. */
  preset(): InputPreset {
    return {
      barcode: this.found() ? this.lookup?.barcode : undefined,
      ingredients: this.found() ? undefined : this.ingredients ?? undefined,
      nutrition: this.found() ? undefined : this.nutrition ?? undefined,
      front: this.found() ? undefined : this.front ?? undefined,
      name: qs<HTMLInputElement>(this.root, ".pin-name").value.trim() || undefined,
      brand: qs<HTMLInputElement>(this.root, ".pin-brand").value.trim() || undefined,
    };
  }

  async applyPreset(preset: InputPreset): Promise<void> {
    if (preset.name) qs<HTMLInputElement>(this.root, ".pin-name").value = preset.name;
    if (preset.brand) qs<HTMLInputElement>(this.root, ".pin-brand").value = preset.brand;
    if (preset.ingredients) this.setFile("ingredients", preset.ingredients, false);
    if (preset.nutrition) this.setFile("nutrition", preset.nutrition, false);
    if (preset.front) this.setFile("front", preset.front, false);
    if (preset.ingredients || preset.nutrition || preset.front) this.showPhotos(true);
    if (preset.barcode) await this.lookupCode(preset.barcode);
  }

  /** Open the camera straight away (the Scan buttons).
   *  Live scanning needs https; over plain http (a phone on the office
   *  Wi-Fi) the phone's camera app takes a photo and we decode that. */
  async startScan(): Promise<void> {
    if (!cameraAvailable()) {
      qs<HTMLInputElement>(this.root, ".pin-barcode-camera").click();
      return;
    }
    const code = await scanBarcode(`Scan the ${this.opts.title.toLowerCase()} barcode`);
    if (code) await this.lookupCode(code);
  }

  /* ---------------- markup ---------------- */

  private build(): HTMLElement {
    const { tag, title, subtitle, tone = "neutral" } = this.opts;
    const id = this.id;
    return html(`
<section class="pin card tone-${tone}">
  <header class="pin-head">
    <span class="pin-tag">${escapeHtml(tag)}</span>
    <div class="pin-titles"><h3>${escapeHtml(title)}</h3><p>${escapeHtml(subtitle)}</p></div>
    <span class="pin-ready" aria-hidden="true">${icon("tick")}</span>
  </header>

  <div class="pin-found" hidden></div>

  <div class="pin-barcode">
    <label class="field-label" for="${id}-code">Barcode</label>
    <div class="field">
      ${icon("keyboard")}
      <input id="${id}-code" class="pin-code" type="text" inputmode="numeric" autocomplete="off"
             placeholder="Type barcode number" aria-describedby="${id}-status">
      <button class="icon-btn pin-go" type="button" aria-label="Look up barcode">${icon("arrow")}</button>
    </div>
    <div class="pin-actions">
      <button class="btn soft pin-scan" type="button">${icon("scan")}<span>Scan barcode</span></button>
      <button class="btn soft pin-photo-btn" type="button">${icon("image")}<span>From gallery</span></button>
      <input type="file" class="pin-barcode-photo" accept="image/*" hidden>
      <input type="file" class="pin-barcode-camera" accept="image/*" capture="environment" hidden>
    </div>
    <p class="pin-status" id="${id}-status" role="status" hidden></p>
  </div>

  <button class="pin-toggle" type="button" aria-expanded="false">
    ${icon("camera")}<span>Not found? Add label photos</span>
  </button>

  <div class="pin-photos" hidden>
    <div class="drop drop-ingredients" tabindex="0" role="button" aria-label="Choose the ingredients photo">
      <input type="file" class="in-pick" accept="image/*" hidden>
      <input type="file" class="in-cam" accept="image/*" capture="environment" hidden>
      <img class="drop-preview" alt="">
      <div class="drop-body">
        ${icon("upload")}
        <p><strong>Ingredients panel</strong> <em class="req">required</em></p>
        <small>Tap to choose a photo</small>
      </div>
      <button class="drop-cam" type="button" aria-label="Take the ingredients photo with the camera">${icon("camera")}</button>
    </div>
    <div class="drop drop-nutrition small" tabindex="0" role="button" aria-label="Choose the nutrition panel photo">
      <input type="file" class="in-pick" accept="image/*" hidden>
      <input type="file" class="in-cam" accept="image/*" capture="environment" hidden>
      <img class="drop-preview" alt="">
      <div class="drop-body">
        ${icon("upload")}
        <p><strong>Nutrition panel</strong> <em>optional</em></p>
        <small>Salt, sugar and fat per 100 g</small>
      </div>
      <button class="drop-cam" type="button" aria-label="Take the nutrition panel photo with the camera">${icon("camera")}</button>
    </div>
    <div class="drop drop-front small" tabindex="0" role="button" aria-label="Choose the front of pack photo">
      <input type="file" class="in-pick" accept="image/*" hidden>
      <input type="file" class="in-cam" accept="image/*" capture="environment" hidden>
      <img class="drop-preview" alt="">
      <div class="drop-body">
        ${icon("image")}
        <p><strong>Front of pack</strong> <em class="req">required</em></p>
        <small>For claims like "No added colour"</small>
      </div>
      <button class="drop-cam" type="button" aria-label="Take the front of pack photo with the camera">${icon("camera")}</button>
    </div>
    <label class="field-label" for="${id}-name">Product name <em>optional: read from the front photo if left blank</em></label>
    <div class="field"><input id="${id}-name" class="pin-name" type="text" autocomplete="off"
         placeholder="e.g. Garam Masala 100 g"></div>
    <label class="field-label" for="${id}-brand">Brand <em>optional: read from the front photo if left blank</em></label>
    <div class="field"><input id="${id}-brand" class="pin-brand" type="text" autocomplete="off"
         placeholder="e.g. Khetika"></div>
  </div>
</section>`);
  }

  /* ---------------- behaviour ---------------- */

  private wire(): void {
    const root = this.root;
    const code = qs<HTMLInputElement>(root, ".pin-code");
    qs(root, ".pin-go").addEventListener("click", () => void this.lookupCode(code.value));
    code.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        void this.lookupCode(code.value);
      }
    });
    // Editing the number invalidates the previous result.
    code.addEventListener("input", () => {
      if (this.lookup && this.lookup.barcode !== code.value.replace(/\D/g, "")) this.setLookup(null);
      else this.refresh();
    });

    qs(root, ".pin-scan").addEventListener("click", () => void this.startScan());

    const photo = qs<HTMLInputElement>(root, ".pin-barcode-photo");
    qs(root, ".pin-photo-btn").addEventListener("click", () => photo.click());
    // Gallery pick and camera-app photo are decoded the same way.
    for (const input of [photo, qs<HTMLInputElement>(root, ".pin-barcode-camera")]) {
      input.addEventListener("change", async () => {
        const file = input.files?.[0];
        input.value = "";
        if (!file) return;
        this.setStatus("busy", `<span class="spin"></span>Reading the barcode from the photo…`);
        try {
          await this.lookupCode(await scanFromPhoto(file));
        } catch (error) {
          this.setStatus(
            "err",
            escapeHtml(error instanceof Error ? error.message : "No barcode found.") +
              " Fill the frame with the bars, or type the number.",
          );
          this.showPhotos(true);
        }
      });
    }

    qs(root, ".pin-toggle").addEventListener("click", () => {
      this.showPhotos(qs(root, ".pin-photos").hidden);
    });

    qs<HTMLInputElement>(root, ".pin-name").addEventListener("input", () => this.opts.onChange());

    this.wireDrop(qs(root, ".drop-ingredients"), "ingredients");
    this.wireDrop(qs(root, ".drop-nutrition"), "nutrition");
    this.wireDrop(qs(root, ".drop-front"), "front");
  }

  private wireDrop(zone: HTMLElement, slot: PhotoSlot): void {
    const input = qs<HTMLInputElement>(zone, ".in-pick");
    const camera = qs<HTMLInputElement>(zone, ".in-cam");
    zone.addEventListener("click", () => input.click());
    qs(zone, ".drop-cam").addEventListener("click", (event) => {
      event.stopPropagation(); // the zone itself opens the gallery
      camera.click();
    });
    camera.addEventListener("click", (event) => event.stopPropagation());
    input.addEventListener("click", (event) => event.stopPropagation());
    camera.addEventListener("change", () => {
      const file = camera.files?.[0];
      camera.value = "";
      if (file) this.setFile(slot, file);
    });
    zone.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        input.click();
      }
    });
    input.addEventListener("change", () => {
      const file = input.files?.[0];
      input.value = "";
      if (file) this.setFile(slot, file);
    });
    for (const name of ["dragenter", "dragover"] as const) {
      zone.addEventListener(name, (event) => {
        event.preventDefault();
        zone.classList.add("over");
      });
    }
    for (const name of ["dragleave", "drop"] as const) {
      zone.addEventListener(name, (event) => {
        event.preventDefault();
        zone.classList.remove("over");
      });
    }
    zone.addEventListener("drop", (event) => {
      const file = event.dataTransfer?.files[0];
      if (file) this.setFile(slot, file);
    });
  }

  /** A photo was picked or taken: offer to crop it to the panel, then use it.
   *  `crop` is false for photos carried over from another screen, already framed. */
  private setFile(slot: PhotoSlot, file: File, crop = true): void {
    const zone = qs(this.root, ".drop-" + slot);
    if (!file.type.startsWith("image/")) {
      zone.classList.add("bad");
      qs(zone, "small").textContent = "That isn't an image. Use a PNG, JPG or WEBP photo.";
      return;
    }
    if (!crop) {
      this.useFile(slot, file);
      return;
    }
    void cropPhoto(file, CROP_TITLES[slot]).then((chosen) => {
      if (chosen) this.useFile(slot, chosen); // Cancel keeps whatever was there before
    });
  }

  private useFile(slot: PhotoSlot, file: File): void {
    const zone = qs(this.root, ".drop-" + slot);
    zone.classList.remove("bad");
    zone.classList.add("filled");
    qs<HTMLImageElement>(zone, ".drop-preview").src = URL.createObjectURL(file);
    qs(zone, "small").textContent = file.name + " · preparing…";
    // Not ready until the photo is shrunk: a full-size camera photo would make
    // the upload too large for the server.
    this[slot] = null;
    const token = (this.shrinking[slot] = (this.shrinking[slot] ?? 0) + 1);
    this.refresh();
    void shrinkPhoto(file).then((small) => {
      if (this.shrinking[slot] !== token) return; // a newer photo replaced this one
      this[slot] = small;
      qs(zone, "small").textContent = file.name + " · tap to replace";
      this.refresh();
    });
  }

  /** Latest photo being shrunk per slot, so a slow one never overwrites a newer pick. */
  private shrinking: Partial<Record<PhotoSlot, number>> = {};

  private showPhotos(open: boolean): void {
    qs(this.root, ".pin-photos").hidden = !open;
    const toggle = qs(this.root, ".pin-toggle");
    toggle.setAttribute("aria-expanded", String(open));
    qs(toggle, "span").textContent = open ? "Hide label photos" : "Not found? Add label photos";
  }

  private setStatus(tone: StatusTone, markup: string): void {
    const box = qs(this.root, ".pin-status");
    box.className = "pin-status" + (tone ? " " + tone : "");
    box.innerHTML = markup;
    box.hidden = !markup;
  }

  async lookupCode(raw: string): Promise<void> {
    const code = raw.replace(/\D/g, "");
    if (!/^\d{8,14}$/.test(code)) {
      this.setStatus("err", "A barcode is 8 to 14 digits, e.g. 8904043927053.");
      return;
    }
    if (/^(\d{8}|\d{12}|\d{13})$/.test(code) && !validBarcode(code)) {
      this.setStatus("err", `${escapeHtml(code)} doesn't add up: its last digit (the check digit) is wrong. Check for a typo.`);
      return;
    }
    qs<HTMLInputElement>(this.root, ".pin-code").value = code;
    this.setStatus("busy", `<span class="spin"></span>Looking up ${escapeHtml(code)} in Open Food Facts…`);
    try {
      this.setLookup(await lookupProduct(code));
    } catch (error) {
      this.setLookup(null);
      this.setStatus("err", escapeHtml(describeFailure(error)));
    }
  }

  private setLookup(result: ProductLookup | null): void {
    this.lookup = result;
    const box = qs(this.root, ".pin-found");
    const barcode = qs(this.root, ".pin-barcode");

    if (result?.status === "found") {
      const group = result.nova_group as NovaGroup | null;
      const thumb = result.image_front_url
        ? `<img src="${escapeHtml(result.image_front_url)}" alt="" loading="lazy">`
        : `<span class="found-thumb-empty">${icon("leaf")}</span>`;
      box.innerHTML =
        `<div class="found-thumb">${thumb}</div>` +
        `<div class="found-text">` +
        (result.brand ? `<span class="eyebrow">${escapeHtml(result.brand)}</span>` : "") +
        `<strong>${escapeHtml(result.name || "Product " + result.barcode)}</strong>` +
        `<small>${escapeHtml([result.quantity, "EAN " + result.barcode].filter(Boolean).join(" · "))}</small>` +
        `<span class="found-ok">${icon("check")} Ingredients from ${escapeHtml(result.source || "Open Food Facts")}` +
        (group ? ` · <b style="color:${novaColour(group)}">NOVA ${group}</b> ${escapeHtml(NOVA_SHORT[group])}` : "") +
        `</span></div>` +
        `<button class="link pin-change" type="button">Change</button>` +
        offImages(result);
      qs(box, ".pin-change").addEventListener("click", () => {
        this.setLookup(null);
        const input = qs<HTMLInputElement>(this.root, ".pin-code");
        input.value = "";
        input.focus();
      });
      box.hidden = false;
      barcode.hidden = true;
      this.setStatus("", "");
    } else {
      box.hidden = true;
      barcode.hidden = false;
      if (result) {
        const title = result.name ? `<strong>${escapeHtml(result.name)}</strong> · ` : "";
        this.setStatus("warn", `${title}${escapeHtml(result.message)}`);
        this.showPhotos(true);
      } else {
        this.setStatus("", "");
      }
    }
    if (!qs(this.root, ".pin-photos").hidden) this.showPhotos(true);
    else this.showPhotos(false);
    this.refresh();
  }

  private refresh(): void {
    this.root.classList.toggle("is-found", this.found());
    this.root.classList.toggle("is-ready", this.ready());
    this.opts.onChange();
  }
}
